import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { loadAsset } from 'core/services/asset';
import { moduleRequest } from 'core/services/product';
import { IAsset } from 'types/app/asset';
import { LoadingState } from 'types/domain/base';
import { IKeyArray } from 'types/IKeyArray';

export interface IAssetReducer {
  [key: string]: IAsset;
}

const initialState = {};

/**
 * Whether two parses of the same asset resolved to the same files. Only `urls`
 * (and the video fallback) are compared: `url` walks the candidate list as the
 * retry ladder advances, so comparing it would rewind a laddered asset on every
 * re-parse.
 */
const sameCandidates = (a: IAsset, b: IAsset) =>
  (a.urls || []).join('|') === (b.urls || []).join('|') && a.fallback === b.fallback;

const assetSlice = createSlice({
  name: 'assets',
  initialState,
  reducers: {
    setAssets(state, action: PayloadAction<IKeyArray<IAsset>>) {
      Object.values(action.payload).forEach((asset: IAsset) => {
        const existing = state[asset.id];

        if (!existing) {
          state[asset.id] = asset;
          return;
        }

        // An asset parsed before its entity's files arrived (the module grid holds the
        // first action of every module, so its scene is in the store long before the
        // module is opened) resolves to a poorer url — or to none at all. Keeping that
        // entry meant the later, complete parse was discarded and the slide rendered an
        // <img> with a dead src. A different candidate list means the underlying files
        // changed, so take the new one; its loading/attempt state belonged to the old
        // url, so that is reset with it.
        if (sameCandidates(existing, asset)) return;

        state[asset.id] = { ...existing, ...asset, attempts: 0 };
      });
    },
    updateAsset(state, action: PayloadAction<Partial<IAsset>>) {
      state[action.payload.id] = { ...(state[action.payload.id] || {}), ...action.payload };
    },
    unloadAsset(state, action: PayloadAction<string>) {
      state[action.payload].loading = LoadingState.DEFAULT;
    },
  },
  extraReducers: builder => {
    builder.addCase('asset/load/pending', (state, action: any) => {
      state[action.meta.arg.id].loading = LoadingState.LOADING;
      state[action.meta.arg.id].attempts = (state[action.meta.arg.id].attempts || 0) + 1;
    });
    builder.addCase('asset/load/fulfilled', (state, action: any) => {
      state[action.meta.arg.id].loading = LoadingState.LOADED;
    });
    builder.addCase('asset/load/rejected', (state, action: any) => {
      state[action.meta.arg.id].loading = LoadingState.ERROR;
    });
    builder.addCase(moduleRequest.rejected, (state, action: any) => {
      if (!state[`module_${action.meta.arg.id}`]) state[`module_${action.meta.arg.id}`] = {};
      state[`module_${action.meta.arg.id}`].attempts = (state[`module_${action.meta.arg.id}`].attempts || 0) + 1;
    });
  },
});

export const { setAssets, updateAsset, unloadAsset } = assetSlice.actions;

export default assetSlice.reducer;
